import React, { Component } from 'react'
import { Alert, Button, Card, Checkbox, Drawer, Empty, Modal, Radio, Select, Space, Spin, Steps, Table, Tag, Typography, message } from 'antd'
import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import {
    deleteVibeOwnerPagePermission,
    getUserDeparts,
    getUserListForDropbox,
    getVibeOwnerAccessContext,
    getVibeOwnerAccessProjects,
    getVibeOwnerAccessStatus,
    replaceVibeOwnerPagePermission,
} from '../../request'
import './index.less'

const { Title, Text, Paragraph } = Typography

const formatTime = value => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    const pad = number => String(number).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default class VibeOwnerAccess extends Component {
    state = {
        loading: true, error: '', projects: [], projectId: undefined, context: null, statusContext: null,
        selectedRouteIds: [], accessMode: undefined, subjectId: undefined,
        includeChildren: true, users: [], departments: [], saving: false,
        drawerOpen: false, drawerMode: 'create', editingPermission: null, deletingKey: null,
    }

    componentDidMount() {
        const params = new URLSearchParams(this.props.location?.search || '')
        this.requestedProjectId = params.get('projectId')
        this.statusView = params.get('view') === 'status'
        if (this.statusView) {
            this.loadStatusContext()
            return
        }
        this.loadProjects()
    }

    loadStatusContext = async () => {
        if (!/^\d+$/.test(this.requestedProjectId || '')) {
            this.setState({ loading: false, error: '应用信息不完整，请返回工作台后重试' })
            return
        }
        this.setState({ loading: true, error: '' })
        try {
            const statusContext = await getVibeOwnerAccessStatus({ projectId: this.requestedProjectId })
            this.setState({ loading: false, statusContext })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用状态加载失败，请稍后重试' })
        }
    }

    loadProjects = async () => {
        this.setState({ loading: true, error: '' })
        try {
            const projects = await getVibeOwnerAccessProjects() || []
            const requested = projects.find(item => String(item.id) === String(this.requestedProjectId))
            const project = requested || projects[0]
            this.setState({ projects, projectId: project?.id, loading: false }, () => {
                if (project) this.loadContext(project.id)
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用列表加载失败，请稍后重试' })
        }
    }

    loadContext = async (projectId = this.state.projectId) => {
        if (!projectId) return
        this.setState({ loading: true, error: '', projectId })
        try {
            const context = await getVibeOwnerAccessContext({ projectId })
            const pageRoutes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
            this.setState({
                context,
                selectedRouteIds: pageRoutes.map(route => route.id),
                accessMode: undefined,
                subjectId: undefined,
                drawerOpen: false,
                editingPermission: null,
                loading: false,
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '权限设置加载失败，请稍后重试' })
        }
    }

    loadOptions = async mode => {
        this.setState({ accessMode: mode, subjectId: undefined })
        if (mode === 'ALL' || (this.state.users.length && this.state.departments.length)) return
        try {
            const [users, departments] = await Promise.all([
                getUserListForDropbox({ type: 0 }), getUserDeparts({}),
            ])
            this.setState({ users: users || [], departments: departments || [] })
        } catch (error) {
            message.error(error?.message || '授权对象加载失败')
        }
    }

    copyPrompt = async () => {
        try {
            await navigator.clipboard.writeText(this.state.context?.routeSyncPrompt || '')
            message.success('页面同步提示词已复制，请在项目 Codex 中执行')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    openCreateDrawer = () => {
        const routes = (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE')
        this.setState({
            drawerOpen: true, drawerMode: 'create', editingPermission: null,
            selectedRouteIds: routes.map(route => route.id), accessMode: undefined,
            subjectId: undefined, includeChildren: true,
        })
    }

    openEditDrawer = row => {
        this.setState({
            drawerOpen: true, drawerMode: 'edit', editingPermission: row,
            selectedRouteIds: row.routes.map(route => route.id), accessMode: row.subjectType,
            subjectId: row.subjectId, includeChildren: row.includeChildren !== false,
        })
    }

    closeDrawer = () => {
        if (!this.state.saving) this.setState({ drawerOpen: false, editingPermission: null })
    }

    savePermissionGroup = async () => {
        const { projectId, selectedRouteIds, accessMode, subjectId, includeChildren } = this.state
        if (!accessMode) {
            message.warning('请选择访问范围')
            return
        }
        if (!subjectId && accessMode !== 'ALL') {
            message.warning('请选择要授权的员工或部门')
            return
        }
        this.setState({ saving: true })
        try {
            await replaceVibeOwnerPagePermission({
                projectId, routeIds: selectedRouteIds, subjectType: accessMode,
                subjectId: accessMode === 'ALL' ? '' : String(subjectId), includeChildren,
            })
            message.success(this.state.drawerMode === 'edit' ? '权限已更新' : '授权已添加')
            this.setState({ saving: false, drawerOpen: false, editingPermission: null }, () => this.loadContext())
        } catch (error) {
            this.setState({ saving: false })
            message.error(error?.message || '保存失败，请稍后重试')
        }
    }

    allowAllEmployees = () => {
        const routes = (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE')
        Modal.confirm({
            title: '允许全部员工访问所有页面？',
            content: `确认后，全体员工将可以访问当前已登记的 ${routes.length} 个页面。`,
            okText: '确认开放', cancelText: '取消',
            onOk: async () => {
                this.setState({ saving: true })
                try {
                    await replaceVibeOwnerPagePermission({
                        projectId: this.state.projectId,
                        routeIds: routes.map(route => route.id),
                        subjectType: 'ALL', subjectId: '', includeChildren: false,
                    })
                    message.success('已允许全部员工访问所有页面')
                    this.setState({ saving: false, drawerOpen: false }, () => this.loadContext())
                } catch (error) {
                    this.setState({ saving: false })
                    message.error(error?.message || '保存失败，请稍后重试')
                    throw error
                }
            },
        })
    }

    deletePermissionGroup = row => {
        const explicitCount = this.getPermissionRows(this.state.context,
            (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE'))
            .filter(item => item.subjectType !== 'OWNER').length
        Modal.confirm({
            title: `删除“${row.subjectName}”的页面权限？`,
            content: <div><Paragraph>将撤销该对象对当前应用 {row.routes.length} 个页面的访问权限。</Paragraph>
                {explicitCount === 1 && <Alert type="warning" showIcon message="这是最后一条授权，删除后应用将恢复为未配置权限状态。" />}</div>,
            okText: '确认删除', okButtonProps: { danger: true }, cancelText: '取消',
            onOk: async () => {
                this.setState({ deletingKey: row.key })
                try {
                    await deleteVibeOwnerPagePermission({
                        projectId: this.state.projectId, subjectType: row.subjectType,
                        subjectId: row.subjectId || '',
                    })
                    message.success('权限已删除')
                    this.setState({ deletingKey: null }, () => this.loadContext())
                } catch (error) {
                    this.setState({ deletingKey: null })
                    message.error(error?.message || '删除失败，请稍后重试')
                    throw error
                }
            },
        })
    }

    getPermissionRows = (context, routes) => {
        if (!context) return []
        const grouped = new Map()
        routes.forEach(route => {
            ;(route.permissions || []).forEach(permission => {
                if (permission.subjectType === 'USER'
                    && String(permission.subjectId) === String(context.ownerUserId)) return
                const key = `${permission.subjectType}:${permission.subjectId || ''}`
                const existing = grouped.get(key) || {
                    key,
                    subjectType: permission.subjectType,
                    subjectId: permission.subjectId || '',
                    subjectName: permission.subjectName || permission.subjectId,
                    includeChildren: permission.includeChildren,
                    routes: [],
                    createdAt: permission.createdAt,
                }
                existing.routes.push(route)
                if (permission.createdAt && (!existing.createdAt || String(permission.createdAt) < String(existing.createdAt))) {
                    existing.createdAt = permission.createdAt
                }
                grouped.set(key, existing)
            })
        })
        return [{
            key: 'OWNER', subjectType: 'OWNER',
            subjectName: context.ownerUserNick || '项目创建人',
            routes, createdAt: context.createTime,
        }, ...Array.from(grouped.values())]
    }

    renderPermissionDrawer = routes => {
        const editing = this.state.drawerMode === 'edit'
        const typeName = { ALL: '全部员工', USER: '指定员工', DEPARTMENT: '指定部门' }
        const ownerUserId = String(this.state.context?.ownerUserId || '')
        return <Drawer
            title={editing ? '编辑页面访问权限' : '新增页面访问权限'}
            width={560} visible={this.state.drawerOpen} onClose={this.closeDrawer}
            destroyOnClose maskClosable={!this.state.saving}
            footer={<div className="vibe-owner-access-drawer-footer"><Button onClick={this.closeDrawer} disabled={this.state.saving}>取消</Button>
                <Button type="primary" loading={this.state.saving}
                    disabled={!this.state.selectedRouteIds.length || !this.state.accessMode || (!this.state.subjectId && this.state.accessMode !== 'ALL')}
                    onClick={this.savePermissionGroup}>保存</Button></div>}>
        <div className="vibe-owner-access-drawer-intro"><Text type="secondary">选择访问对象和可访问页面，保存后立即生效。</Text></div>
        <div className="vibe-owner-access-routes"><Title level={5}>可访问页面</Title>
            <Checkbox.Group value={this.state.selectedRouteIds} onChange={value => this.setState({ selectedRouteIds: value })}>
                <Space direction="vertical">{routes.map(route => <Checkbox key={route.id} value={route.id}>{route.routeName} <Text type="secondary">{route.pathPattern}</Text></Checkbox>)}</Space>
            </Checkbox.Group></div>
        <div className="vibe-owner-access-grant"><Title level={5}>访问范围</Title>
            {editing ? <div className="vibe-owner-access-locked-subject"><Text strong>{this.state.editingPermission?.subjectName}</Text>
                <Tag>{typeName[this.state.accessMode]}</Tag><Text type="secondary">如需更换对象，请删除后重新授权。</Text></div>
                : <Radio.Group value={this.state.accessMode} onChange={event => this.loadOptions(event.target.value)}>
                    <Radio value="ALL">全部员工</Radio><Radio value="USER">指定员工</Radio><Radio value="DEPARTMENT">指定部门</Radio>
                </Radio.Group>}
            {!editing && this.state.accessMode === 'USER' && <Select showSearch placeholder="选择员工" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                optionFilterProp="label"
                options={this.state.users.filter(user => String(user.USER_ID || user.userId) !== ownerUserId)
                    .map(user => ({ value: user.USER_ID || user.userId, label: user.USER_NICK || user.userNick || user.USER_NAME }))} />}
            {this.state.accessMode === 'DEPARTMENT' && <Space>{!editing && <Select showSearch placeholder="选择部门" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                optionFilterProp="label" options={this.state.departments.map(dept => ({ value: dept.DING_DEPT_ID || dept.dingDeptId, label: dept.DEPT_NAME || dept.deptName }))} />}
                <Checkbox checked={this.state.includeChildren} onChange={event => this.setState({ includeChildren: event.target.checked })}>包含下级部门</Checkbox></Space>}
            <Paragraph type="secondary">项目创建人始终可以访问，无需单独授权。</Paragraph>
        </div>
        {!editing && <Button block loading={this.state.saving} onClick={this.allowAllEmployees}>一键允许全部员工访问所有页面</Button>}
    </Drawer>
    }

    renderPermissionList = (context, routes) => {
        const rows = this.getPermissionRows(context, routes)
        const typeMeta = {
            OWNER: { color: 'gold', text: '项目创建人' },
            USER: { color: 'blue', text: '员工' },
            DEPARTMENT: { color: 'purple', text: '部门' },
            ALL: { color: 'green', text: '全部员工' },
        }
        const columns = [{
            title: '访问对象', dataIndex: 'subjectName', width: 220,
            render: (value, row) => <div className="vibe-owner-access-subject"><Text strong>{value}</Text>
                <Tag color={typeMeta[row.subjectType]?.color}>{typeMeta[row.subjectType]?.text || row.subjectType}</Tag>
                {row.subjectType === 'DEPARTMENT' && row.includeChildren ? <Text type="secondary">包含下级部门</Text> : null}</div>,
        }, {
            title: '可访问页面', dataIndex: 'routes',
            render: routeList => <div className="vibe-owner-access-page-tags">{routeList.map(route =>
                <Tag key={route.id}>{route.routeName}<span>{route.pathPattern}</span></Tag>)}</div>,
        }, {
            title: '添加时间', dataIndex: 'createdAt', width: 170,
            render: value => <Text type="secondary">{formatTime(value)}</Text>,
        }, {
            title: '操作', key: 'action', width: 150, fixed: 'right',
            render: (_, row) => row.subjectType === 'OWNER' ? <Text type="secondary">默认权限</Text> : <Space>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => this.openEditDrawer(row)}>编辑</Button>
                <Button type="link" size="small" danger icon={<DeleteOutlined />} loading={this.state.deletingKey === row.key}
                    onClick={() => this.deletePermissionGroup(row)}>删除</Button>
            </Space>,
        }]
        return <div className="vibe-owner-access-list">
            <div className="vibe-owner-access-list-heading">
                <div><Title level={5}>当前访问权限</Title><Text type="secondary">共 {rows.length} 个访问对象，覆盖 {routes.length} 个页面</Text></div>
                <Space><Button disabled={!context?.entryUrl} onClick={() => window.location.assign(context.entryUrl)}>打开应用</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreateDrawer}>新增授权</Button></Space>
            </div>
            <Table rowKey="key" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 760 }} />
        </div>
    }

    renderStatus = () => {
        const { loading, error, statusContext } = this.state
        return (
        <div className="vibe-owner-access-page"><Card className="vibe-owner-access-status">
            <Spin spinning={loading}>
                <SafetyCertificateOutlined className="vibe-owner-access-status-icon" />
                <Title level={3}>你还不能访问这个页面</Title>
                {error ? <Alert type="error" showIcon message={error} /> : <>
                    <Paragraph type="secondary">{statusContext?.projectName || '该应用'}正在准备访问设置，或你尚未获得页面权限。</Paragraph>
                    {statusContext?.ownerUserNick && <Paragraph>应用负责人：{statusContext.ownerUserNick}</Paragraph>}
                </>}
                <Space className="vibe-owner-access-status-actions">
                    <Button type="primary" href="/portal/dashboard">返回工作台</Button>
                    <Button disabled={!statusContext?.entryUrl} onClick={() => window.location.assign(statusContext.entryUrl)}>重新检查访问权限</Button>
                    {error && <Button onClick={this.loadStatusContext}>重试加载</Button>}
                </Space>
            </Spin>
        </Card></div>
        )
    }

    render() {
        if (this.statusView) return this.renderStatus()
        const { loading, error, projects, projectId, context } = this.state
        const routes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
        return <div className="vibe-owner-access-page">
            <div className="vibe-owner-access-heading"><Title level={3}>应用权限设置</Title>
                <Text type="secondary">查看并维护员工可访问的应用页面。</Text></div>
            <Card bordered={false}>
                <Spin spinning={loading}>
                    {error && <Alert type="error" showIcon message={error} action={<Button onClick={this.loadProjects}>重试</Button>} />}
                    {!error && !loading && !projects.length && <Empty description="你还没有可配置的内部应用" />}
                    {!!projects.length && <>
                        <div className="vibe-owner-access-project"><Text>我创建的应用</Text>
                            <Select value={projectId} onChange={this.loadContext} style={{ minWidth: 260 }}
                                options={projects.map(item => ({ value: item.id, label: item.projectName }))} /></div>
                        {!context?.accessConfigured && <Steps current={routes.length ? 1 : 0} items={[{ title: '同步应用页面' }, { title: '设置访问范围' }, { title: '完成配置' }]} />}
                        {context && !routes.length && <Alert className="vibe-owner-access-alert" type="warning" showIcon
                            message="还差一步即可开放应用"
                            description="尚未检测到应用页面。复制提示词到项目 Codex 执行，完成后重新检测。"
                            action={<Space direction="vertical"><Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制页面同步提示词</Button>
                                <Button icon={<ReloadOutlined />} onClick={() => this.loadContext()}>我已同步，重新检测</Button></Space>} />}
                        {!!routes.length && this.renderPermissionList(context, routes)}
                        {!!routes.length && this.renderPermissionDrawer(routes)}
                    </>}
                </Spin>
            </Card>
        </div>
    }
}
